import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudyEditor } from '../StudyEditor';
import { getStudyById, getUserProfile, getOrganization, logout } from '../../services/firebase';
import { Study, UserProfile, Organization } from '../../types';
import { Icons } from '../ui/Icons';

export const StudyEditorRoute: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const [study, setStudy] = useState<Study | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      if (!studyId) {
        setError('No study ID provided');
        setLoading(false);
        return;
      }

      try {
        // Load user profile first (required for editing)
        const userId = localStorage.getItem('userId');
        if (!userId) {
          setError('You must be logged in to edit studies');
          setLoading(false);
          return;
        }

        const userProfile = await getUserProfile(userId);
        if (!userProfile) {
          setError('User profile not found');
          setLoading(false);
          return;
        }
        setUser(userProfile);

        if (userProfile.orgId) {
          const org = await getOrganization(userProfile.orgId);
          setOrganization(org);
        }

        // If creating a new study, skip loading study data
        if (studyId === 'new') {
          setLoading(false);
          return;
        }

        // Load study data
        const studyData = await getStudyById(studyId);
        if (!studyData) {
          setError('Study not found');
          setLoading(false);
          return;
        }

        // Verify user has permission to edit this study
        if (studyData.authorId !== userProfile.uid && studyData.orgId !== userProfile.orgId) {
          setError('You do not have permission to edit this study');
          setLoading(false);
          return;
        }

        setStudy(studyData);
        setLoading(false);
      } catch (err) {
        console.error('Error loading study:', err);
        setError('Failed to load study');
        setLoading(false);
      }
    };

    loadData();
  }, [studyId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.Loader className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
          <p className="text-gray-600">Loading study...</p>
        </div>
      </div>
    );
  }

  if (error || !user || (!study && studyId !== 'new')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Icons.X className="w-12 h-12 text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Study not found or access denied'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <StudyEditor
      userUid={user.uid}
      userName={user.displayName || 'Anonymous'}
      orgId={user.orgId || ''}
      user={user}
      organization={organization}
      initialData={study}
      onLogout={logout}
      onClose={() => navigate('/dashboard')}
      onSaveSuccess={() => navigate('/dashboard')}
    />
  );
};
